import { useEffect, useRef, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import api from '../lib/api';
import { useI18n } from '../i18n/I18nContext';
import { LanguageSwitcher } from '../components/LanguageSwitcher';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Volleyball, Star, Upload, Plus, Trash2 } from 'lucide-react';

const NETWORKS = ['instagram', 'tiktok', 'linkedin', 'youtube', 'other'];
const MAX_LINKS = 5;

export default function AmbassadorProfile() {
  const { token } = useParams();
  const { t } = useI18n();
  const [profile, setProfile] = useState(null);
  const [error, setError] = useState(false);
  const [links, setLinks] = useState([{ network: 'instagram', url: '' }, { network: 'tiktok', url: '' }]);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [photoFile, setPhotoFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = () => {
    api.get(`/ambassador-profile/${token}`)
      .then((r) => {
        setProfile(r.data);
        if (r.data.links?.length) {
          const filled = r.data.links.map((l) => ({ network: l.network, url: l.url }));
          while (filled.length < 2) filled.push({ network: 'instagram', url: '' });
          setLinks(filled);
        }
        if (r.data.has_photo) {
          setPhotoPreview(`${process.env.REACT_APP_BACKEND_URL}/api/ambassador-photo/${r.data.application_id}?pt=${token}`);
        }
      })
      .catch(() => setError(true));
  };
  useEffect(load, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const onFileChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(f.type)) { toast.error(t('ambProfile.photoInvalid')); return; }
    if (f.size > 5 * 1024 * 1024) { toast.error(t('ambProfile.photoTooBig')); return; }
    setPhotoFile(f);
    setPhotoPreview(URL.createObjectURL(f));
  };

  const uploadPhoto = async () => {
    if (!photoFile) return;
    setUploading(true);
    const fd = new FormData();
    fd.append('file', photoFile);
    try {
      await api.post(`/ambassador-profile/${token}/photo`, fd);
      toast.success(t('ambProfile.photoUploaded'));
      setPhotoFile(null);
      load();
    } catch (err) {
      toast.error(typeof err.response?.data?.detail === 'string' ? err.response.data.detail : t('common.error'));
    } finally {
      setUploading(false);
    }
  };

  const setLink = (i, patch) => setLinks((ls) => ls.map((l, j) => (j === i ? { ...l, ...patch } : l)));

  const saveLinks = async () => {
    setSaving(true);
    try {
      await api.put(`/ambassador-profile/${token}`, { links: links.filter((l) => l.url.trim()) });
      toast.success(t('ambProfile.saved'));
      load();
    } catch (err) {
      toast.error(typeof err.response?.data?.detail === 'string' ? err.response.data.detail : t('common.error'));
    } finally {
      setSaving(false);
    }
  };

  const inputCls = 'h-12 rounded-2xl bg-nuit-900 border-nuit-600 text-creme placeholder:text-brume focus:ring-4 focus:ring-or-400/40 font-bold';

  return (
    <div className="min-h-screen bg-nuit-900 px-4 py-10 sm:py-14" data-testid="ambassador-profile-page">
      <div className="max-w-2xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-2 font-fredoka font-semibold text-lg text-or-400">
            <span className="w-9 h-9 rounded-full bg-or-400 flex items-center justify-center">
              <Volleyball className="w-5 h-5 text-nuit-950" strokeWidth={2.5} />
            </span>
            {t('ambProfile.title')}
          </div>
          <LanguageSwitcher />
        </div>

        {error ? (
          <div className="bg-nuit-800 border border-nuit-700 rounded-3xl p-8 text-center" data-testid="ambassador-profile-error">
            <p className="text-creme font-bold">{t('ambProfile.notFound')}</p>
            <Link to="/contact" className="text-or-400 font-bold hover:underline text-sm" data-testid="ambassador-profile-contact-link">{t('footer.contact')}</Link>
          </div>
        ) : !profile ? (
          <p className="text-brume font-bold text-center" data-testid="ambassador-profile-loading">{t('common.loading')}</p>
        ) : (
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }}
            className="bg-nuit-800 border border-nuit-700 rounded-3xl p-6 sm:p-10 space-y-8">
            <div>
              <p className="text-creme font-bold text-lg" data-testid="ambassador-profile-name">{profile.name}</p>
              <p className="text-sm text-brume leading-relaxed mt-2">{t('ambProfile.intro')}</p>
            </div>

            {profile.profile_approved ? (
              <p className="bg-green-400/10 border border-green-400/40 text-green-300 rounded-2xl p-4 text-sm font-bold" data-testid="ambassador-profile-status">{t('ambProfile.approved')}</p>
            ) : profile.profile_status === 'rejected' ? (
              <p className="bg-rose-400/10 border border-rose-400/40 text-rose-300 rounded-2xl p-4 text-sm font-bold" data-testid="ambassador-profile-status">{t('ambProfile.rejected')}</p>
            ) : profile.profile_status === 'pending_review' ? (
              <p className="bg-amber-400/10 border border-amber-400/40 text-amber-300 rounded-2xl p-4 text-sm font-bold" data-testid="ambassador-profile-status">{t('ambProfile.pendingReview')}</p>
            ) : null}

            <div>
              <p className="text-sm font-bold text-creme mb-2">{t('ambProfile.photoLabel')}</p>
              <div className="flex items-center gap-4 flex-wrap">
                {photoPreview ? (
                  <img src={photoPreview} alt={profile.name} data-testid="ambassador-photo-preview"
                    className="w-24 h-24 rounded-full object-cover border-2 border-or-400/50" />
                ) : (
                  <span className="w-24 h-24 rounded-full bg-or-400/15 flex items-center justify-center" data-testid="ambassador-photo-placeholder">
                    <Star className="w-8 h-8 text-or-400" />
                  </span>
                )}
                <div className="space-y-2">
                  <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden"
                    data-testid="ambassador-photo-input" onChange={onFileChange} />
                  <Button type="button" variant="outline" data-testid="ambassador-photo-choose"
                    onClick={() => fileRef.current?.click()}
                    className="rounded-full font-bold border-nuit-600 text-creme hover:bg-nuit-700">
                    {t('ambProfile.photoChoose')}
                  </Button>
                  {photoFile && (
                    <Button type="button" onClick={uploadPhoto} disabled={uploading} data-testid="ambassador-photo-upload"
                      className="rounded-full bg-or-400 hover:bg-or-300 text-nuit-950 font-bold ml-2">
                      <Upload className="w-4 h-4 mr-2" />{uploading ? t('common.sending') : t('ambProfile.photoUpload')}
                    </Button>
                  )}
                  <p className="text-xs text-brume">{t('ambProfile.photoHelp')}</p>
                </div>
              </div>
            </div>

            <div>
              <p className="text-sm font-bold text-creme mb-1">{t('ambProfile.linksTitle')}</p>
              <p className="text-xs text-brume mb-4">{t('ambProfile.linksHelp')}</p>
              <div className="space-y-3">
                {links.map((l, i) => (
                  <div key={i} className="flex gap-2 items-center">
                    <Select value={l.network} onValueChange={(v) => setLink(i, { network: v })}>
                      <SelectTrigger data-testid={`ambassador-link-network-${i}`}
                        className="w-36 h-12 rounded-2xl bg-nuit-900 border-nuit-600 text-creme font-bold">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {NETWORKS.map((n) => (
                          <SelectItem key={n} value={n} data-testid={`ambassador-link-network-${i}-${n}`}>{t(`ambProfile.networks.${n}`)}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Input value={l.url} onChange={(e) => setLink(i, { url: e.target.value })}
                      placeholder={t('ambProfile.url')} data-testid={`ambassador-link-url-${i}`} className={inputCls} />
                    {links.length > 2 && (
                      <button type="button" onClick={() => setLinks((ls) => ls.filter((_, j) => j !== i))}
                        data-testid={`ambassador-link-remove-${i}`}
                        className="w-9 h-9 shrink-0 rounded-full hover:bg-rose-950 inline-flex items-center justify-center text-rose-400">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
              {links.length < MAX_LINKS && (
                <Button type="button" variant="outline" onClick={() => setLinks((ls) => [...ls, { network: 'instagram', url: '' }])}
                  data-testid="ambassador-link-add"
                  className="mt-4 rounded-full font-bold border-nuit-600 text-creme hover:bg-nuit-700">
                  <Plus className="w-4 h-4 mr-2" />{t('ambProfile.addLink')}
                </Button>
              )}
            </div>

            <Button onClick={saveLinks} disabled={saving} data-testid="ambassador-save-links"
              className="w-full h-14 rounded-full bg-or-400 hover:bg-or-300 text-nuit-950 font-bold text-base">
              {saving ? t('common.sending') : t('ambProfile.save')}
            </Button>
          </motion.div>
        )}
      </div>
    </div>
  );
}
